import { parseApiDateUtc } from '@/lib/api-date'
import { getUserTimeZone } from '@/lib/user-timezone'

/** Только время (ЧЧ:ММ) для момента UTC из API — в часовом поясе пользователя. */
export function formatUserTimeFromIso(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = parseApiDateUtc(iso)
  if (!d) return null
  return d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getUserTimeZone(),
  })
}

function ymdInTz(instant: Date, timeZone: string): { y: number; m: number; d: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = formatter.formatToParts(instant)
  const map: Record<string, string> = {}
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value
  }
  return {
    y: Number(map.year),
    m: Number(map.month),
    d: Number(map.day),
  }
}

function calendarKey(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function addDaysToYmd(y: number, m: number, d: number, delta: number): { y: number; m: number; d: number } {
  const x = new Date(Date.UTC(y, m - 1, d + delta))
  return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate() }
}

/**
 * Время начала смены для подписей «на смене с …» / «Смена с …».
 * Если начало в тот же календарный день, что и «сейчас» в TZ пользователя — только ЧЧ:ММ.
 * Если вчера — «вчера, ЧЧ:ММ»; иначе — «5 апр., ЧЧ:ММ» (год — если не текущий).
 */
export function formatShiftStartedAtForDisplay(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = parseApiDateUtc(iso)
  if (!d) return null
  const tz = getUserTimeZone()
  const now = new Date()
  const startYmd = ymdInTz(d, tz)
  const todayYmd = ymdInTz(now, tz)
  const startKey = calendarKey(startYmd.y, startYmd.m, startYmd.d)
  const todayKey = calendarKey(todayYmd.y, todayYmd.m, todayYmd.d)
  const yest = addDaysToYmd(todayYmd.y, todayYmd.m, todayYmd.d, -1)
  const yesterdayKey = calendarKey(yest.y, yest.m, yest.d)

  const timeOnly = d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  })

  if (startKey === todayKey) return timeOnly
  if (startKey === yesterdayKey) return `вчера, ${timeOnly}`

  const datePart = d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    timeZone: tz,
    ...(startYmd.y !== todayYmd.y ? { year: 'numeric' } : {}),
  })
  return `${datePart}, ${timeOnly}`
}
